/*
Default theme + page styling for <tosi-doc-system>.

This is the stylesheet the component injects so a generated static page looks like
a finished doc site without depending on any app bootstrap. It uses the legacy
`--brand-color` / `--background` / `--spacing` CSS-variable family that the doc
browser and markdown styles are built on.

Colors are computed from a small set of base colors via tosijs `Color` math, so the
palette can eventually be driven entirely by a few attributes on the element
(accent/background/text) — most of the palette is derived from `accent`.
*/

import { navSectionStyleSpec } from './nav-styles.js'
import { highlightStyleSpec } from './highlight-styles.js'
import {
  XinStyleSheet,
  XinStyleRule,
  vars,
  Color,
  invertLuminance,
} from 'tosijs'
import { icons, svg2DataUrl } from '../icons.js'

// The sidebar's geometry, shared by the hydrated layout and the pre-hydration one.
// These mirror what <tosi-sidenav> is given by doc-browser (navSize / minSize): the
// sidebar is `SIDEBAR_WIDTH` wide and collapses behind a toggle below
// `SIDEBAR_BREAKPOINT`. Keep them in step, or the page jumps as it hydrates.
export const SIDEBAR_WIDTH = 200
export const SIDEBAR_BREAKPOINT = 600

export interface DocSystemTheme {
  /** brand / accent color — most of the palette is derived from this */
  accent?: string
  background?: string
  text?: string
  buttonBg?: string
  inputBg?: string
  /** code-editor surface (`--code-bg`); neutral off-white by default, not brand-tinted. */
  /**
   * Background behind code blocks. Default `#fdfdfd` (light) — dark mode inverts it.
   *
   * **The syntax-highlighting palette does not track this.** Those eleven token colours are
   * fixed literals, contrast-checked against the DEFAULT background in both modes; there is
   * no recomputation that keeps hand-chosen colours legible against an arbitrary one. Set a
   * dark `codeBg` for light mode and the worst token measures **1.71:1** against WCAG AA's
   * 4.5. Override it and you own the contrast — retheme the tokens too, via the
   * `--token-*` variables (each is a `varDefault`). Scoped by a test in
   * `doc-system-styles.test.ts`.
   */
  codeBg?: string
}

/**
 * WCAG 2 contrast ratio between two opaque colors. Throws on a translucent one: its real
 * contrast depends on what is behind it, and ignoring alpha measured the old 40%-opacity
 * placeholder at 4.74:1 where axe, compositing it, measured 1.88:1.
 */
export function contrastRatio(a: Color, b: Color): number {
  if (a.RGBA[3] < 1 || b.RGBA[3] < 1)
    throw new Error('contrastRatio: composite a translucent color first')
  const lum = (c: Color) => {
    const [r, g, bl] = c.RGBA.map((v) =>
      v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
    )
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl
  }
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * `color`, darkened (on a light `bg`) or brightened (on a dark one) just far enough to reach
 * `min` contrast against `bg`, so it keeps as much of its hue as AA allows.
 */
export function readableOn(color: Color, bg: Color, min = 4.5): Color {
  const darkBg = contrastRatio(bg, Color.black) < contrastRatio(bg, Color.white)
  // Measured on the hex the stylesheet will actually carry: rounding to 8-bit channels
  // can drop a ratio just over `min` to just under it.
  const shipped = (c: Color) => Color.fromCss(c.html)
  let c = shipped(color.opaque)
  for (let step = 0; step < 20 && contrastRatio(c, bg) < min; step++) {
    c = shipped(darkBg ? c.brighten(0.1) : c.darken(0.1))
  }
  return contrastRatio(c, bg) < min ? bg.contrasting() : c
}

/** Compute the full set of `:root` color variables from a few base colors. */
export function docSystemColors(theme: DocSystemTheme = {}): XinStyleRule {
  const brandColor = Color.fromCss(theme.accent ?? '#d92270')
  return {
    _textColor: theme.text ?? '#222',
    _brandColor: brandColor,
    _background: theme.background ?? '#fafafa',
    _buttonBg: theme.buttonBg ?? '#fdfdfd',
    _inputBg: theme.inputBg ?? '#fdfdfd',
    _backgroundShaded: '#f5f5f5',
    _navBg: brandColor.rotate(30).desaturate(0.5).brighten(0.9),
    _barColor: brandColor.opacity(0.4),
    _focusColor: brandColor.opacity(0.7),
    /*
    Readable, not faint (#3520). This was the brand at 40% opacity, which an axe scan measured
    at 1.88:1 on the input (#eda7c5 on #fdfdfd) where AA asks 4.5:1, and it colours the doc
    header's search trigger, a real label, not just a hint.
    */
    _placeholderColor: readableOn(
      brandColor,
      Color.fromCss(theme.inputBg ?? '#fdfdfd')
    ),
    /*
    `contrasting()` rather than a hand-tuned tint.

    `brandColor.rotate(30).brighten(0.9)` produced a warm off-white (#fbeae9) that sat at
    **4.09:1** on the brand bar — under AA for the normal-size nav links in it. Every
    brighten value short of pure white also misses (0.96 lands at 4.49), so the tint had
    to go; it was imperceptible anyway. `contrasting()` picks the readable extreme for
    whatever the brand currently is, so this cannot drift the next time the brand moves.
    Dark mode is unaffected: invertLuminance flips this to near-black on the brightened
    bar, which is already high-contrast.
    */
    _brandTextColor: brandColor.contrasting(),
    _insetBg: brandColor.rotate(45).brighten(0.8),
    // The brand, made readable on the inset (blockquote) background, the darker of the two
    // surfaces code sits on. The raw brand measured 3.72:1 there (#3520).
    // Checked against the code background too: a custom `codeBg` can be the darker one.
    _codeColor: readableOn(
      readableOn(brandColor, brandColor.rotate(45).brighten(0.8)),
      Color.fromCss(theme.codeBg ?? '#fdfdfd')
    ),
    // Neutral off-white (not brand-tinted); dark mode inverts its luminance to
    // off-black, so the editor surface stays a clean neutral in both themes.
    _codeBg: theme.codeBg ?? '#fdfdfd',
    _linkColor: brandColor.rotate(-30).darken(0.5),
    _shadowColor: '#0004',
    _menuBg: '#fafafa',
    _menuItemActiveColor: '#000',
    _menuItemIconActiveColor: '#000',
    _menuItemActiveBg: '#aaa',
    _menuItemHoverBg: '#eee',
    _menuItemColor: '#222',
    _menuSeparatorColor: '#2224',
    _menuShadow: '0 4px 8px #0004',
    _scrollThumbColor: '#0006',
    _scrollBarColor: '#0001',
    _inputBorderShadow: 'inset 0 0 2px #0006',
  }
}

/** Build the full doc-system stylesheet for a given base theme. */
export function docSystemStyleSpec(theme: DocSystemTheme = {}): XinStyleSheet {
  const colors = docSystemColors(theme)
  const brandColor = Color.fromCss(theme.accent ?? '#d92270')
  return {
    '@import':
      'https://fonts.googleapis.com/css2?family=Aleo:ital,wght@0,100..900;1,100..900&famiSpline+Sans+Mono:ital,wght@0,300..700;1,300..700&display=swap',
    ':root': {
      _fontFamily: "'Aleo', sans-serif",
      _codeFontFamily: "'Spline Sans Mono', monospace",
      _fontSize: '16px',
      _codeFontSize: '14px',
      ...colors,
      // Bridge the tosijs component palette (`--tosi-*`) onto the doc-system's color
      // family. Defined as REFERENCES, so a component reading `var(--tosi-bg)` follows
      // the site theme automatically — including dark mode, where `invertLuminance()`
      // flips the referenced `--background`/`--text-color`/… in `.darkmode`. Without
      // this the doc-system never set `--tosi-*`, so components fell back to their
      // baked-in light defaults (e.g. `var(--tosi-bg, #fff)` → white <tosi-table> in
      // dark mode). Colors only — component metrics keep their own defaults.
      _tosiAccent: vars.brandColor,
      _tosiBg: vars.background,
      _tosiText: vars.textColor,
      _tosiBgInset: vars.insetBg,
      _spacing: '10px',
      _lineHeight: 'calc(var(--font-size) * 1.6)',
      _h1Scale: '2',
      _h2Scale: '1.5',
      _h3Scale: '1.25',
      _touchSize: '32px',
      _headerHeight:
        'calc( var(--line-height) * var(--h2-scale) + var(--spacing) * 2 )',
      // The content column's width. ONE definition, read by both the hydrated
      // doc-browser (doc-browser.ts) and the pre-hydration `:not(:defined)` layout
      // below — if these two ever disagree, the page jumps on hydration.
      _docContentMaxWidth: '44em',
    },
    /*
    Per-page layout, driven by `layout:` in a doc's metadata (see `Doc.layout`).

    Both are variable overrides rather than new layout rules, which is what keeps them cheap
    and non-invasive: the measure is already `--doc-content-max-width`, so a page opts out by
    changing one value. Nothing here runs for a corpus that never sets `layout`.
    */
    'tosi-doc-system[data-layout="full-width"]': {
      _docContentMaxWidth: 'none',
    },
    /*
    `full-screen` — no measure, and no chrome.

    These rules are for the PRE-HYDRATION page, whose markup is a plain `<nav class="doc-nav">`
    with no inline styles, so hiding it here works and works on first paint. Once the bundle
    loads, the nav becomes a slotted node inside `<tosi-sidenav>` carrying its own inline
    `display`, and CSS can no longer touch it — the doc-browser sets `sidenav.navHidden`
    instead. Different DOM before and after hydration, so genuinely two rules rather than a
    belt-and-braces duplicate.
    */
    'tosi-doc-system[data-layout="full-screen"]': {
      _docContentMaxWidth: 'none',
    },
    'tosi-doc-system[data-layout="full-screen"] .doc-content': {
      /*
      A definite height, so a child asking for `height: 100%` gets one.

      "The content is the viewport" is the promise this layout makes, and a demo, a canvas or an
      embedded app is the reason to use it — all of which want to FILL, not to sit in a tall
      box. Without a height here `100%` on a child resolves against `auto` and collapses to its
      content.
      */
      height: '100%',
      /*
      The gutter and the scroller both go through VARIABLES, because `.doc-content`'s `padding`
      and `overflow` are INLINE styles set by the doc-browser — declared here they would simply
      lose.

      `overflow` used to be declared directly, so this layout's scroller never existed: the
      `height: 100%` landed and the `overflow: auto` did not, giving a full-screen page whose
      content is taller than the box a clipped, unscrollable article rather than the scroll
      container the layout promises (tosijs-ui#119). `padding` had the note and `overflow` did
      not, one line apart.
      */
      _docContentPadding: '0',
      _docContentOverflow: 'auto',
    },
    /*
    `.doc-nav.doc-nav` — the class twice, on purpose.

    `tosi-doc-system:not(:defined) .doc-nav` (the pre-hydration layout, further down this same
    sheet) has IDENTICAL specificity to the single-class form, so which one wins is decided by
    source order, and it sits later. Winning on position works until someone reorders the spec,
    at which point the nav quietly comes back on full-screen pages with nothing to point at.
    Repeating the class costs one selector token and settles it on specificity, wherever these
    rules end up living.
    */
    'tosi-doc-system[data-layout="full-screen"] > .doc-nav.doc-nav, tosi-doc-system[data-layout="full-screen"] > .doc-navbar.doc-navbar':
      {
        display: 'none',
      },
    /*
    `header: false` — no header bar on this page (#226). One variable takes the bar's space
    away everywhere it is reserved (the pre-hydration inset, the nav's top, the hydrated
    column), and the bar itself is hidden before hydration (`::before`) and after (`header`).
    */
    'tosi-doc-system[data-no-header]': {
      _headerHeight: '0px',
    },
    'tosi-doc-system[data-no-header]::before, tosi-doc-system[data-no-header] header':
      {
        display: 'none',
      },
    '.darkmode': {
      ...invertLuminance(colors),
      /*
      Lift the brand color for dark backgrounds.

      `invertLuminance` flips the neutrals but leaves a saturated brand hue roughly where
      it was, so one accent has to serve both a near-white and a near-black page — and a
      pink dark enough to clear AA on `#fafafa` (4.56:1) lands at 4.4:1 on `#050505`,
      just under. Brightening it in dark mode is the standard resolution: the hue is the
      brand, the luminance belongs to the background it sits on.
      */
      _brandColor: brandColor.brighten(0.18),
      _codeColor: readableOn(
        readableOn(
          brandColor.brighten(0.18),
          brandColor.rotate(45).brighten(0.8).inverseLuminance
        ),
        Color.fromCss(theme.codeBg ?? '#fdfdfd').inverseLuminance
      ),
      _placeholderColor: readableOn(
        brandColor.brighten(0.18),
        Color.fromCss(theme.inputBg ?? '#fdfdfd').inverseLuminance
      ),
      _shadowColor: brandColor.opacity(0.5),
      _menuShadow: `0 0 0 2px ${brandColor.opacity(0.75)}`,
      _menuSeparatorColor: brandColor.opacity(0.5),
    },
    '.high-contrast': {
      filter: 'contrast(2)',
    },
    '*': {
      boxSizing: 'border-box',
      scrollbarColor: `${vars.scrollThumbColor} ${vars.scrollBarColor}`,
      scrollbarWidth: 'thin',
    },
    body: {
      fontFamily: vars.fontFamily,
      fontSize: vars.fontSize,
      margin: '0',
      lineHeight: vars.lineHeight,
      background: vars.background,
      _tosiTabsSelectedColor: vars.brandColor,
      _tosiTabsBarColor: vars.brandTextColor,
      _menuItemIconColor: vars.brandColor,
      color: vars.textColor,
    },
    // <tosi-doc-system> is a light-DOM block that fills the viewport; the doc
    // browser container inside it provides the flex column + 100vh.
    'tosi-doc-system': {
      display: 'block',
    },
    'tosi-doc-system tosi-sidenav::part(nav)': {
      background: vars.navBg,
    },

    // ── Pre-hydration layout (`:not(:defined)`) ──────────────────────────────
    //
    // An UNDEFINED custom element is `display: inline`, so before the bundle loads
    // the pre-rendered page — which already contains the nav tree, the content and
    // the code blocks — stacks as bare text with no chrome. That reflow-on-hydrate
    // is why the page used to hide its own body (`body{opacity:0}`) until the
    // bundle arrived: on a cheap phone that's ~4.5s of blank screen for content
    // that was in the HTML at ~300ms.
    //
    // Instead, lay the static page out AS THOUGH the chrome were already there:
    // the content sits in a scrolling rectangle inset by the header, and by the
    // sidebar once the viewport is wide enough. Hydration then only ADDS the
    // chrome — and `doc-browser` ADOPTS this very `.doc-content` node rather than
    // re-rendering it, so with the same box metrics below, nothing moves at all.
    //
    // These rules evaporate by themselves the moment the element upgrades.
    'tosi-doc-system:not(:defined)': {
      display: 'block',
      position: 'fixed',
      inset: `${vars.headerHeight} 0 0 0`,
      overflow: 'auto',
    },
    // The header bar: painted, not populated — its contents (brand, links) arrive
    // with hydration, in this exact space.
    'tosi-doc-system:not(:defined)::before': {
      content: '""',
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      height: vars.headerHeight,
      background: vars.brandColor,
    },
    // Same box the hydrated doc-browser gives this node (see doc-browser.ts), so
    // adoption is a no-op geometrically.
    'tosi-doc-system:not(:defined) .doc-content': {
      display: 'block',
      maxWidth: vars.docContentMaxWidth,
      margin: 'auto',
      padding: 'var(--doc-content-padding, 0 1em)',
      // Must match doc-browser's inline style EXACTLY, `overflow` included: it
      // establishes a block formatting context, which stops the <h1>'s top margin
      // collapsing out of the box. Without it the static content sits 10px lower
      // than the hydrated content, and the page nudges as it comes alive.
      // Same variable as the inline style, so a page that overrides one overrides
      // both — otherwise the override would land only after hydration and the page
      // would visibly change shape (#119).
      overflow: 'var(--doc-content-overflow, hidden)',
    },
    // The header-bar links are icon links; without the icon font they'd render as
    // stray text in the bar. They arrive with hydration.
    'tosi-doc-system:not(:defined) .doc-navbar': {
      display: 'none',
    },
    // A `test` block is an example's ASSERTIONS. The live example keeps them behind
    // a tab — a reader is never shown them — so rendering them statically both
    // misrepresents the page and adds height that disappears the moment the example
    // mounts. (`:has()` is a progressive enhancement: where it isn't supported the
    // block simply stays visible, which is what happens today anyway.)
    'tosi-doc-system:not(:defined) pre:has(> code.language-test)': {
      display: 'none',
    },
    // Below the sidebar breakpoint (side-nav goes compact under
    // `doc-browser`'s minSize) the nav is behind a toggle, so it isn't shown.
    'tosi-doc-system:not(:defined) .doc-nav': {
      display: 'none',
    },
    // Wide enough for the sidebar: reserve its column, and show the REAL nav tree
    // in it. It's a plain <ul> of real links, every section expanded when there is no
    // JavaScript to collapse one (no `tosi-js` on <html>) — a no-JS reader gets a
    // working site, and hydration merely upgrades it in place.
    [`@media (min-width: ${SIDEBAR_BREAKPOINT}px)`]: {
      'tosi-doc-system:not(:defined)': {
        left: `${SIDEBAR_WIDTH}px`,
      },
      'tosi-doc-system:not(:defined) .doc-nav': {
        display: 'block',
        position: 'fixed',
        left: '0',
        top: vars.headerHeight,
        bottom: '0',
        width: `${SIDEBAR_WIDTH}px`,
        overflow: 'auto',
        background: vars.navBg,
        // NO padding. The hydrated nav sits in a <tosi-sidenav> whose own padding is 0 —
        // the visual inset comes from `.doc-link`'s padding, which both states share. A
        // `padding: spacing` here shifted every static nav link 10px right, so the whole
        // column jumped left the instant the bundle loaded. Whatever this block sets, the
        // upgraded component must already set too, or hydration is not "purely additive".
        padding: '0',
      },
      // NOTE: no `a` / `ul` rules here on purpose. There used to be a second,
      // hand-authored copy of the nav's link styling under `:not(:defined)`, because
      // the static generator emitted a bare <a> while the hydrated nav emits
      // <a class="doc-link">. It drifted (2.5px vs 5px/15px padding, a brand-coloured
      // underline that only the static copy had), so every page painted one nav and
      // then reflowed into a different one. The generator now emits `doc-link` too, so
      // the SHARED rules below style both states and nothing moves on hydration.
      //
      // Only genuinely pre-hydration-specific rules belong in this block — the fixed
      // positioning above, which the upgraded component takes over itself.
    },
    '.center': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    'input, button, select, textarea': {
      fontFamily: vars.fontFamily,
      fontSize: vars.fontSize,
      color: 'currentColor',
      background: vars.inputBg,
    },
    select: {
      WebkitAppearance: 'none',
      appearance: 'none',
    },
    header: {
      background: vars.brandColor,
      color: vars.brandTextColor,
      _textColor: vars.brandTextColor,
      _linkColor: vars.brandTextColor,
      display: 'flex',
      alignItems: 'center',
      padding: '0 var(--spacing)',
      lineHeight: 'calc(var(--line-height) * var(--h1-scale))',
      height: vars.headerHeight,
      whiteSpace: 'nowrap',
    },
    h1: {
      color: vars.brandColor,
      fontSize: 'calc(var(--font-size) * var(--h1-scale))',
      lineHeight: 'calc(var(--line-height) * var(--h1-scale))',
      fontWeight: '400',
      borderBottom: `4px solid ${vars.barColor}`,
      margin: `${vars.spacing} 0 ${vars.spacing200}`,
      padding: 0,
    },
    'header .site-name': {
      color: vars.brandTextColor,
      whiteSpace: 'nowrap',
      fontWeight: '700',
      fontSize: 'calc(var(--font-size) * var(--h2-scale))',
      lineHeight: 'calc(var(--line-height) * var(--h2-scale))',
      margin: 'calc(var(--spacing) * var(--h2-scale)) 0',
    },
    h2: {
      color: vars.brandColor,
      fontSize: 'calc(var(--font-size) * var(--h2-scale))',
      lineHeight: 'calc(var(--line-height) * var(--h2-scale))',
      margin: 'calc(var(--spacing) * var(--h2-scale)) 0',
    },
    h3: {
      fontSize: 'calc(var(--font-size) * var(--h3-scale))',
      lineHeight: 'calc(var(--line-height) * var(--h3-scale))',
      margin: 'calc(var(--spacing) * var(--h3-scale)) 0',
    },
    'input[type=search]': {
      borderRadius: 99,
    },
    blockquote: {
      position: 'relative',
      background: vars.insetBg,
      margin: '0 48px 56px 0',
      borderRadius: vars.spacing,
      padding: 'var(--spacing) calc(var(--spacing) * 2)',
      filter: `drop-shadow(0px 1px 1px ${vars.shadowColor})`,
    },
    'blockquote > :first-child': {
      marginTop: '0',
    },
    'blockquote > :last-child': {
      marginBottom: '0',
    },
    'blockquote::before': {
      content: '" "',
      display: 'block',
      width: 1,
      height: 1,
      border: '10px solid transparent',
      borderTopColor: vars.insetBg,
      borderRightColor: vars.insetBg,
      position: 'absolute',
      bottom: -20,
      right: 24,
    },
    'blockquote::after': {
      content: '" "',
      width: 48,
      height: 48,
      display: 'block',
      bottom: -48,
      right: -24,
      position: 'absolute',
      background: svg2DataUrl(icons.tosi(), undefined, undefined, 2),
    },
    a: {
      textDecoration: 'none',
      color: vars.linkColor,
      opacity: '0.9',
      borderBottom: '1px solid var(--brand-color)',
    },
    'button, select, .clickable': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'ease-out 0.2s',
      /*
      Set the COLOUR, do not redefine the palette token (#150).

      This was `--text-color: var(--brand-color); color: var(--text-color)`, which redefines a
      GLOBAL theme token on an element selector. `--text-color` is what the whole palette is
      built from (`--tosi-text: var(--text-color)`), so from any `<button>` downward it stopped
      meaning "the theme's text colour" and meant the brand colour instead.

      That reaches into anything embedded. An editor deriving its chrome from the page
      (`--editor-text: var(--tosi-text)`) resolved correctly in the document and to the brand
      colour on its own slotted toolbar buttons — measured at oklab L≈0.27 against an L 0.16
      bar in dark mode, unreadable, with correct body text beside it.

      Redefining an inherited token on an element selector poisons the subtree for every
      consumer of that token, not just for this rule. Setting `color` says the same thing and
      says only it.
      */
      color: vars.brandColor,
      textDecoration: 'none',
      background: vars.buttonBg,
      padding: '0 calc(var(--spacing) * 1.25)',
      border: 'none',
      borderRadius: 'calc(var(--spacing) * 0.5)',
    },
    'button, select, .clickable, input': {
      lineHeight: 'calc(var(--line-height) + var(--spacing))',
    },
    'input, textarea': {
      border: 'none',
      outline: 'none',
      borderRadius: 'calc(var(--spacing) * 0.5)',
      boxShadow: vars.inputBorderShadow,
    },
    input: {
      padding: '0 calc(var(--spacing) * 1.5)',
    },
    '::placeholder': {
      color: vars.placeholderColor,
    },
    img: {
      verticalAlign: 'middle',
    },
    'button:hover, .clickable:hover': {
      boxShadow: 'inset 0 0 0 2px var(--brand-color)',
    },
    'button:active, .clickable:active': {
      background: vars.brandColor,
      color: vars.brandTextColor,
    },
    label: {
      display: 'inline-flex',
      gap: 'calc(var(--spacing) * 0.5)',
      alignItems: 'center',
    },
    '.elastic': {
      flex: '1 1 auto',
      overflow: 'hidden',
      position: 'relative',
    },
    // Scope the icon styling (currentColor fill, click-through) to actual icons
    // and SVGs inside buttons — a blanket `svg` rule sets pointer-events:none on
    // every SVG, which breaks interactive SVG-based UIs (maps, charts, diagrams).
    'svg.tosi-icon, button svg': {
      fill: 'currentColor',
      pointerEvents: 'none',
    },
    '[aria-selected]': {
      background: '#08835820',
    },
    ':disabled': {
      opacity: '0.5',
      filter: 'saturate(0)',
      pointerEvents: 'none',
    },
    pre: {
      background: vars.codeBg,
      padding: vars.spacing,
      borderRadius: 'calc(var(--spacing) * 0.25)',
      overflow: 'auto',
      fontSize: vars.codeFontSize,
      lineHeight: 'calc(var(--font-size) * 1.2)',
    },
    'pre, code': {
      fontFamily: vars.codeFontFamily,
      // Same defect as the button rule above (#150): this redefined the global
      // `--text-color` token rather than setting a colour, so everything inside a `<pre>`
      // — including every syntax-highlighting token span — inherited a palette where the
      // theme's text colour had been replaced by the brand colour.
      color: vars.codeColor,
    },
    /*
    Prism token colours for STATIC code blocks — TWO palettes, because there are two
    backgrounds (blocker B2 of the 1.15.0 review).

    The rules themselves now live in `highlight-styles.ts` — see there for the palette and
    the measured contrast figures. What is worth keeping HERE is why it shipped broken.

    The first version applied VSCode **Dark+** literals unconditionally. `--code-bg` is
    `#fdfdfd` in light mode, which is the DEFAULT — the pre-paint script adds `darkmode` only
    on an explicit or system preference — so every token type failed WCAG AA on the page most
    readers see: function/class-name at 1.39:1, operator 1.46:1, property 1.47:1. Four were
    effectively invisible.

    What let it ship is worse than the mistake. The BOOK palette in `book-html.ts` was
    contrast-checked against `#f6f8fa`; for the site I wrote "the site's code sits on a dark
    code background" and treated that sentence as the check. It was false, and it appeared in
    four places including the CHANGELOG — a justification standing in for a measurement.
    */
    /*
    ONE copy, in `highlight-styles.ts`. It used to live inline here, which meant the token
    palette was reachable only by importing the entire doc-site chrome — so a standalone
    `<tosi-highlight>` rendered correct token markup in flat black. Spread, not duplicated.
    */
    ...highlightStyleSpec,
    '.transparent, .iconic': {
      background: 'none',
    },
    '.iconic': {
      padding: '0',
      fontSize: '150%',
      height: 'calc(var(--line-height) + var(--spacing))',
      lineHeight: 'calc(var(--line-height) + var(--spacing))',
      width: 'calc(var(--line-height) + var(--spacing))',
      textAlign: 'center',
    },
    '.transparent:hover, .iconic:hover': {
      background: '#0002',
      boxShadow: 'none',
      color: vars.textColor,
    },
    '.transparent:active, .iconic:active': {
      background: '#0004',
      boxShadow: 'none',
      color: vars.textColor,
    },
    '.current, .nav-branch-head:has(.current)': {
      background: vars.background,
    },
    '.doc-link': {
      cursor: 'pointer',
      borderBottom: 'none',
      transition: '0.15s ease-out',
      marginLeft: '10px',
      padding: 'calc(var(--spacing) * 0.5) calc(var(--spacing) * 1.5)',
    },
    '.doc-link:not(.current):hover': {
      background: vars.background,
    },
    '.doc-link:not(.current)': {
      opacity: '0.8',
      marginLeft: 0,
    },
    'tosi-example': {
      margin: 'var(--spacing) 0',
    },
    'tosi-example [part=editors]': {
      background: vars.insetBg,
    },
    "[class*='icon-'], tosi-icon": {
      color: 'currentcolor',
      pointerEvents: 'none',
    },
    "[class*='icon-']": {
      verticalAlign: 'middle',
    },
    // Every other block element brings its own margin; without one a table sat flush
    // against a following blockquote, whose top margin is 0 by design (#3553).
    table: {
      borderCollapse: 'collapse',
      margin: `${vars.spacing} 0 ${vars.spacing200} 0`,
    },
    thead: {
      background: vars.brandColor,
      color: vars.brandTextColor,
    },
    tbody: {
      background: vars.background,
    },
    'tr:nth-child(2n)': {
      background: vars.backgroundShaded,
    },
    'th, td': {
      padding: 'calc(var(--spacing) * 0.5) var(--spacing)',
    },
    // Nav lists carry semantics only — no list styling. Hierarchy indentation
    // comes from left-padding on each section's child list.
    '.doc-nav ul, .doc-nav li': {
      listStyle: 'none',
      margin: 0,
      padding: 0,
    },
    '.doc-nav li': {
      display: 'flex',
      flexDirection: 'column',
    },
    '.doc-nav': {
      display: 'flex',
      flexDirection: 'column',
    },
    ...navSectionStyleSpec,
    // Declarative header link list — shown for no-JS, removed on hydration.
    '.doc-navbar': {
      listStyle: 'none',
      display: 'flex',
      flexWrap: 'wrap',
      gap: vars.spacing,
      margin: 0,
      padding: vars.spacing,
    },
  }
}
